import { useState, useEffect } from 'react';

import { useFormOptions } from '../../hooks';
import {
  formatDate, dateToString, firstDayOfPreviousMonth, lastDayOfPreviousMonth,
} from '../../utils/date';
import { getOnePreSettlement } from '../../api';
import { hasAccess, ROLE_CARRIER, ROLE_OPERATOR } from '../../utils/access';
import {
  EMPTY_BAG_VALUE,
  FREQUENCY_OPTIONS, MONTH_DAYS_OPTIONS, REQUEST_TYPE_OPTIONS, START_DATE, WEEK_DAYS_OPTIONS,
} from './constants';
import { loadImpc } from '../../utils/impcUtil';

export const useFieldValues = (id, excludeAllMailClassOption = false, setAllMailClassSelected, setImpcSelected) => {
  const fields = ['date_type', 'dropdown_carrier', 'mail_class', 'postal_operator', 'impc'];
  const [options, optionsLoaded, updateOptions] = useFormOptions(fields, excludeAllMailClassOption);
  const [operator, updateOperator] = useState({});
  const [operatorCopy, updateOperatorCopy] = useState(operator);

  const [values, setValues] = useState({
    startDate: formatDate(firstDayOfPreviousMonth()),
    endDate: formatDate(lastDayOfPreviousMonth()),
    requestType: REQUEST_TYPE_OPTIONS[0],
    excludeClosedTransit: false,
  });
  const setValuesHandler = (newValues, isCopy = false) => {
    if (isCopy) {
      newValues.endDate = formatDate(newValues.endDate);
      newValues.startDate = formatDate(newValues.startDate);
    } else if (newValues.requestType.value === 'SCHEDULED') {
      if (newValues.endDate === formatDate(lastDayOfPreviousMonth())) {
        newValues.endDate = null;
      }
      if (newValues.startDate === formatDate(firstDayOfPreviousMonth())) {
        newValues.startDate = null;
      }
    } else if (newValues.requestType.value === 'IMMEDIATE') {
      if (newValues.startDate === formatDate(START_DATE)) {
        newValues.startDate = formatDate(firstDayOfPreviousMonth());
      }
      if (!newValues.endDate) {
        newValues.endDate = formatDate(lastDayOfPreviousMonth());
      }
    }
    if (setAllMailClassSelected && newValues.mailClass && (newValues.mailClass.some(item => item.value === 'All')
      || options.mailClass.filter(item => item.value !== EMPTY_BAG_VALUE).length === newValues.mailClass.length)) {
      setAllMailClassSelected(true);
      newValues.mailClass = options.mailClass.filter(item => item.value !== EMPTY_BAG_VALUE);
    }
    setValues(newValues);
    if (newValues.operator) {
      updateOperatorCopy(newValues.operator);
    }
  };

  const changeImpcSelected = () => {
    const selectedValues = values.impc && values.impc.length;
    const availableOptions = options.impc && options.impc.length;
    const selectedValuesNotEmpty = values.impc && values.impc.length !== 0;
    const availableOptionsNotEmpty = options.impc && options.impc.length !== 0;
    if ((selectedValues === availableOptions) && (selectedValuesNotEmpty && availableOptionsNotEmpty)) {
      setImpcSelected(true);
    } else {
      setImpcSelected(false);
    }
  };

  useEffect(() => {
    setValues({ ...values, impc: [] });
    loadImpc(operator, options, updateOptions);
  }, [operator]);

  useEffect(() => {
    loadImpc(operatorCopy, options, updateOptions);
  }, [operatorCopy]);

  useEffect(() => {
    changeImpcSelected();
  }, [values.impc && values.impc.length]);

  useEffect(() => {
    changeImpcSelected();
  }, [options.impc && options.impc.length]);

  useEffect(() => {
    const loadPresettlement = async () => {
      if (id && optionsLoaded) {
        const response = await getOnePreSettlement(id);
        const requestType = REQUEST_TYPE_OPTIONS.find(item => item.value === response.requestType);
        const frequency = FREQUENCY_OPTIONS.find(item => item.value === response.frequency);
        const runDay = WEEK_DAYS_OPTIONS.find(item => item.value === response.runDay) || MONTH_DAYS_OPTIONS.find(item => item.value === response.runDay);
        delete response.id;

        setValuesHandler({
          ...response,
          requestType,
          frequency,
          runDay,
          startDate: response.startDate,
          endDate: response.endDate,
          status: undefined,
        }, true);
      }
    };

    loadPresettlement();
  }, [id, optionsLoaded]);

  const inputChange = (e) => {
    const { name, value } = e.target;
    setValuesHandler({ ...values, [name]: value });
  };

  const selectChange = (value, { name }) => {
    const newValues = { ...values, [name]: value };
    if (name === 'frequency') {
      newValues.runDay = null;
    }
    if (name === 'operator') {
      updateOperator(value);
    }
    setValuesHandler(newValues);
  };

  const dateChange = name => (selectedDay, modifiers, dayPickerInput) => {
    const input = dayPickerInput.getInput().value.trim();
    const isDisabled = modifiers.disabled === true;
    if (selectedDay && (selectedDay.getFullYear() <= 9999) && input && input !== '' && !isDisabled) {
      setValues({ ...values, [name]: dateToString(selectedDay) });
    } else {
      setValues({ ...values, [name]: null });
    }
  };

  return [values, setValuesHandler, options, inputChange, selectChange, dateChange, optionsLoaded];
};

export const useValidations = (values, receptacles, submitted) => {
  const [validations, updateValidations] = useState({});
  const checkValue = field => values[field] && !!values[field].value;
  const checkListValues = listField => values[listField] && values[listField].length !== 0;

  useEffect(() => {
    let newValidations = {
      all: true,
      requestName: true,
      dateType: true,
      startDate: true,
      endDate: true,
      operator: true,
      carrier: true,
      mailClass: true,
      frequency: true,
      runDay: true,
      receptacleId: true,
      despatchId: true,
      consignmentId: true,
    };
    const tempValues = {
      requestName: !!values.requestName,
    };

    if (receptacles === 'all') {
      tempValues.dateType = checkValue('dateType');
      tempValues.startDate = !!values.startDate;
      tempValues.endDate = !!values.endDate;
      tempValues.operator = checkValue('operator');
      tempValues.carrier = checkValue('carrier');
      tempValues.mailClass = checkListValues('mailClass');

      if (hasAccess([ROLE_CARRIER])) {
        tempValues.carrier = true;
      } else if (hasAccess([ROLE_OPERATOR])) {
        tempValues.operator = true;
      }

      if (values.requestType && values.requestType.value === 'SCHEDULED') {
        tempValues.frequency = checkValue('frequency');
        tempValues.runDay = checkValue('runDay');
      }
    } else {
      const field = `${receptacles}Id`;
      tempValues[field] = !!values[field];
    }

    newValidations.all = !Object.keys(tempValues).filter(item => !tempValues[item]).length;

    if (submitted) {
      newValidations = {
        ...newValidations,
        ...tempValues,
      };
    }

    updateValidations(newValidations);
  }, [values, submitted]);

  return [validations];
};
